import { useState, useEffect } from 'react';
import type { FeatureProperties } from '@/types';

interface TooltipOverlayProps {
    featureProperties: FeatureProperties | null;
    position: { lng: number; lat: number; x?: number; y?: number } | null;
    mapContainer: HTMLElement | null;
}

/**
 * TooltipOverlay component - Displays feature information on hover
 * Requirement: SC-005 - Tooltips display within 200ms
 */
export default function TooltipOverlay({
  featureProperties,
  position,
  mapContainer
}: TooltipOverlayProps): JSX.Element | null {
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!featureProperties || !position || !mapContainer) {
      setVisible(false);
      return;
    }

    // Show tooltip after 200ms delay (meets SC-005 requirement)
    const timer = setTimeout(() => {
      // Calculate pixel position from lng/lat
      const rect = mapContainer.getBoundingClientRect();
      const x = position.x || rect.width / 2;
      const y = position.y || rect.height / 2;

      setTooltipStyle({
        left: `${x + 10}px`, // 10px offset from cursor
        top: `${y - 10}px`
      });
      setVisible(true);
    }, 200);

    return () => clearTimeout(timer);
  }, [featureProperties, position, mapContainer]);

  if (!visible || !featureProperties) {
    return null;
  }

  const { type, label, description, operationalType, fileCount } =
    featureProperties;

  return (
    <div
      className="bn-absolute bn-bg-surface bn-p-300 bn-rounded-200 bn-shadow-lg bn-border bn-border-stroke bn-z-50"
      style={{
        ...tooltipStyle,
        position: 'absolute',
        pointerEvents: 'none',
        maxWidth: '300px'
      }}
    >
      <div className="bn-flex bn-items-center bn-gap-200 bn-mbe-200">
        <span
          className="bn-text-xl"
          aria-hidden="true"
        >
          {type === 'operationalPoint' ? '📍' : '🛤️'}
        </span>
        <h4 className="bn-text-base bn-font-semibold">{label}</h4>
      </div>
      <div>
        <p className="bn-text-sm bn-mbe-200">{description}</p>
        {operationalType && (
          <p className="bn-text-xs bn-text-secondary">
            <strong>Type:</strong> {operationalType}
          </p>
        )}
        {fileCount !== undefined && fileCount > 0 && (
          <p className="bn-text-xs bn-text-secondary">
            <strong>RailML files:</strong> {fileCount}
          </p>
        )}
      </div>
    </div>
  );
}
